// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { getReferenceString } from '@medplum/core';
import type { Bundle, Practitioner, PractitionerRole, ProjectMembership } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { POLARIS_PROVIDER_ROLE_SYSTEM } from '../../constants/appointmentTypes';
import { buildStaffRow, getRoleCode, pickPrimaryRole, type StaffRow } from './staffing-utils';

export interface StaffListQuery {
  page: number; // 1-based
  pageSize: number;
  sort: { column: 'name' | 'lastUpdated'; direction: 'asc' | 'desc' };
  search: string;
}

export interface UseStaffListResult {
  rows: StaffRow[];
  total: number;
  loading: boolean;
  error: string | undefined;
  refresh: () => void;
  reload: () => void;
}

const ROLE_PAGE_SIZE = 200;
const MEMBERSHIP_PAGE_SIZE = 200;

export function useStaffList(query: StaffListQuery): UseStaffListResult {
  const medplum = useMedplum();
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [fetchKey, setFetchKey] = useState(0);
  const noCacheRef = useRef(false);

  const refresh = useCallback(() => {
    noCacheRef.current = false;
    setFetchKey((k) => k + 1);
  }, []);

  const reload = useCallback(() => {
    noCacheRef.current = true;
    setFetchKey((k) => k + 1);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchKey is an intentional trigger counter — it increments to force a re-fetch without being read inside the effect body
  useEffect(() => {
    let active = true;
    const noCache = noCacheRef.current;
    // reset for next call
    noCacheRef.current = false;

    setLoading(true);
    setError(undefined);

    async function load(): Promise<void> {
      const searchOptions = noCache ? { cache: 'no-cache' as const } : undefined;

      // Compute _sort param from query
      let sortParam: string;
      if (query.sort.column === 'name') {
        sortParam = query.sort.direction === 'asc' ? 'name' : '-name';
      } else {
        sortParam = query.sort.direction === 'asc' ? '_lastUpdated' : '-_lastUpdated';
      }

      // Compute search params. _total=accurate is required so Bundle.total
      // reflects the full count across all pages (not just this page's entries).
      const searchParams: Record<string, string> = {
        _count: String(query.pageSize),
        _offset: String((query.page - 1) * query.pageSize),
        _sort: sortParam,
        _total: 'accurate',
      };

      // Server-side search: family:contains covers last-name substring matches.
      // Limitation: given-name-only queries will not match since Medplum's
      // Practitioner search does not support _filter with OR across fields in
      // all configurations. Document as known v1 limitation.
      if (query.search.trim()) {
        searchParams['family:contains'] = query.search.trim();
      }

      // Use search() (returns Bundle) so we get Bundle.total alongside the entries.
      const bundle = await medplum.search('Practitioner', searchParams, searchOptions);
      const practitioners = ((bundle as Bundle<Practitioner>).entry ?? [])
        .map((e) => e.resource)
        .filter((r): r is Practitioner => r !== undefined);

      const bundleTotal = (bundle as Bundle).total ?? 0;

      if (!active) {
        return;
      }

      if (practitioners.length === 0) {
        setRows([]);
        setTotal(bundleTotal);
        return;
      }

      // Fetch PractitionerRole and ProjectMembership only for the current page's
      // Practitioner IDs — avoids fetching all 200+ roles on every page change.
      const practitionerIds = practitioners.map((p) => p.id).filter((id): id is string => Boolean(id));
      const practitionerRefs = practitionerIds.map((id) => `Practitioner/${id}`);

      const [roles, memberships] = await Promise.all([
        medplum.searchResources(
          'PractitionerRole',
          { practitioner: practitionerRefs.join(','), _count: String(ROLE_PAGE_SIZE) },
          searchOptions
        ),
        // Non-admin AccessPolicies don't currently grant read on ProjectMembership.
        // Without this fallback the whole list errors out with "Could not load staff:
        // Forbidden" for any user who reaches Staffing via the role-based admin-panel
        // grant. Degrade gracefully: treat every row as active (default isActive=true
        // in buildStaffRow when membership is undefined). Disable/Enable bots are
        // admin-membership bots, so the action still executes correctly when clicked.
        medplum
          .searchResources(
            'ProjectMembership',
            { profile: practitionerRefs.join(','), _count: String(MEMBERSHIP_PAGE_SIZE) },
            searchOptions
          )
          .catch((err: unknown) => {
            console.warn('[useStaffList] ProjectMembership read denied — falling back to active=true', err);
            return [] as ProjectMembership[];
          }),
      ]);

      const rolesByPractitioner = new Map<string, PractitionerRole[]>();
      for (const role of roles) {
        const ref = role.practitioner?.reference;
        if (!ref) {
          continue;
        }
        const list = rolesByPractitioner.get(ref);
        if (list) {
          list.push(role);
        } else {
          rolesByPractitioner.set(ref, [role]);
        }
      }

      // Keyed by `membership.profile.reference` (e.g. "Practitioner/<id>")
      const membershipByPractitionerRef = new Map<string, ProjectMembership>();
      for (const membership of memberships) {
        const ref = membership.profile?.reference;
        if (ref) {
          membershipByPractitionerRef.set(ref, membership);
        }
      }

      console.debug('[useStaffList] loaded page', query.page, {
        practitionerCount: practitioners.length,
        bundleTotal,
        practitionerRoleCount: roles.length,
        membershipCount: memberships.length,
      });

      const nextRows = practitioners.map((p: Practitioner) => {
        const ref = getReferenceString(p);
        const candidates = ref ? (rolesByPractitioner.get(ref) ?? []) : [];
        const role = pickPrimaryRole(candidates);
        const membership = ref ? membershipByPractitionerRef.get(ref) : undefined;

        if (candidates.length > 0 && getRoleCode(role) === undefined) {
          console.warn('[useStaffList] PractitionerRole(s) present but no Polaris role coding found.', {
            practitionerRef: ref,
            expectedSystem: POLARIS_PROVIDER_ROLE_SYSTEM,
            candidateRoleIds: candidates.map((r) => r.id),
            candidateCodings: candidates.map((r) => r.code?.[0]?.coding),
          });
        }
        if (candidates.length === 0) {
          console.debug('[useStaffList] No PractitionerRole linked to', ref);
        }

        return buildStaffRow(p, role, membership);
      });

      if (!active) {
        return;
      }
      setRows(nextRows);
      setTotal(bundleTotal);
    }

    load()
      .catch((err) => {
        console.error('[useStaffList] Failed to load staff:', err);
        if (active) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [medplum, fetchKey, query.page, query.pageSize, query.sort.column, query.sort.direction, query.search]);

  return { rows, total, loading, error, refresh, reload };
}
